// Cart functionality for Micro Flower Farm theme
// Inspired by Shopify Dawn theme

class Cart {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.bindAddedModal();
    // Initialize cart badge with current cart count from Shopify
    if (typeof window.cartItemCount !== "undefined") {
      this.updateCartBadge(window.cartItemCount);
    }
  }

  bindEvents() {
    // Only bind cart-specific events if we're on the cart page
    if (document.querySelector("[data-cart-item]")) {
      this.watchCartPhotos();
      this.bindTerms();

      // Quantity button events
      document.addEventListener("click", (event) => {
        if (event.target.closest(".quantity-btn-decrease")) {
          event.preventDefault();
          const button = event.target.closest(".quantity-btn-decrease");
          const itemKey = button.dataset.itemKey;
          const change = parseInt(button.dataset.change);
          if (itemKey && !isNaN(change)) {
            this.updateItemQuantity(itemKey, change);
          }
        }

        if (event.target.closest(".quantity-btn-increase")) {
          event.preventDefault();
          const button = event.target.closest(".quantity-btn-increase");
          const itemKey = button.dataset.itemKey;
          const change = parseInt(button.dataset.change);
          if (itemKey && !isNaN(change)) {
            this.updateItemQuantity(itemKey, change);
          }
        }

        if (event.target.closest(".remove-cart-item")) {
          event.preventDefault();
          const button = event.target.closest(".remove-cart-item");
          const itemKey = button.dataset.itemKey;
          if (itemKey) {
            this.removeItem(itemKey);
          }
        }
      });

      // Quantity input events
      document.addEventListener("input", (event) => {
        if (event.target.matches("input[data-item-key]")) {
          const input = event.target;
          const itemKey = input.dataset.itemKey;
          const newQuantity = parseInt(input.value) || 1;

          if (itemKey && !isNaN(newQuantity)) {
            // Debounce the input
            clearTimeout(input.debounceTimer);
            input.debounceTimer = setTimeout(() => {
              this.updateItemQuantityDirect(itemKey, newQuantity);
            }, 500);
          }
        }
      });
    }
  }

  // The terms of service box above Proceed to Checkout (templates/cart.liquid). Ticking it saves the time
  // on the cart as "Terms Accepted At", which shows on the order; unticking clears it. Going to checkout
  // without the tick opens the prompt in snippets/cart-terms-modal.liquid, whose "I accept" ticks the box,
  // saves the time and carries on to checkout. The "terms of service" links open the terms themselves in
  // a dialog on the page; its "I accept" ticks the box too, and carries on to checkout when it was opened
  // from the prompt.
  bindTerms() {
    const box = document.querySelector("[data-cart-terms]");
    const checkout = document.querySelector("[data-cart-checkout]");
    const dialog = document.getElementById("cart-terms-dialog");
    const tos = document.getElementById("cart-tos-dialog");
    if (!box || !checkout) return;

    let saving = Promise.resolve();
    const save = (accepted) => {
      saving = fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { "Terms Accepted At": accepted ? this.termsTimestamp() : "" } }),
      }).catch(() => {});
      return saving;
    };
    const label = checkout.textContent.trim();
    const go = async () => {
      checkout.setAttribute("aria-busy", "true");
      checkout.textContent = "Loading Checkout\u2026";
      await saving;
      window.location.href = checkout.href;
    };
    // Coming back from checkout with the Back button can restore this page as it was left
    window.addEventListener("pageshow", () => {
      checkout.removeAttribute("aria-busy");
      checkout.textContent = label;
    });

    const accept = () => {
      box.checked = true;
      save(true);
    };

    // A dialog that closes on a click outside it, and calls done(returnValue) when it closes
    const open = (which, done) => {
      which.returnValue = "";
      const outside = (e) => {
        if (e.target === which) which.close("cancel");
      };
      which.addEventListener("click", outside);
      which.addEventListener(
        "close",
        () => {
          which.removeEventListener("click", outside);
          done(which.returnValue);
        },
        { once: true }
      );
      which.showModal();
    };

    // Pasted policies use bold-only paragraphs for section names; show them as headings, as the policy
    // pages do (layout/theme.liquid)
    const promoteHeadings = (rte) => {
      rte.querySelectorAll(":scope > p").forEach((p) => {
        const only = p.children.length === 1 && p.firstElementChild.tagName === "STRONG";
        if (only && p.textContent.trim() === p.firstElementChild.textContent.trim()) {
          p.classList.add("policy-heading");
          p.setAttribute("role", "heading");
          p.setAttribute("aria-level", "3");
        }
      });
    };
    // When Liquid didn't have the policy text, load it once from the terms page itself
    let termsLoaded = null;
    const loadTerms = () => {
      const rte = tos.querySelector(".rte");
      if (termsLoaded) return termsLoaded;
      const src = rte.getAttribute("data-terms-src");
      termsLoaded = !src
        ? Promise.resolve()
        : fetch(src, { credentials: "same-origin" })
            .then((r) => r.text())
            .then((html) => {
              const page = new DOMParser().parseFromString(html, "text/html");
              const text =
                page.querySelector(".shopify-policy__body .rte") || page.querySelector(".shopify-policy__body");
              if (text && text.textContent.trim()) rte.innerHTML = text.innerHTML;
            })
            .catch(() => {});
      return termsLoaded.then(() => promoteHeadings(rte));
    };
    if (tos) loadTerms();

    const showTerms = (thenCheckout) => {
      loadTerms();
      const body = tos.querySelector(".terms-text");
      if (body) body.scrollTop = 0;
      open(tos, (value) => {
        if (value !== "accept") return;
        accept();
        if (thenCheckout) go();
      });
    };

    document.querySelectorAll("[data-terms-open]").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (!tos || typeof tos.showModal !== "function") return; // follow the link instead
        event.preventDefault();
        event.stopPropagation(); // don't toggle the checkbox its label wraps
        const fromPrompt = dialog && dialog.contains(link);
        if (fromPrompt) dialog.close("terms");
        showTerms(fromPrompt);
      });
    });

    box.addEventListener("change", () => save(box.checked));
    checkout.addEventListener("click", (event) => {
      event.preventDefault();
      if (box.checked) {
        go();
        return;
      }
      if (!dialog || typeof dialog.showModal !== "function") {
        box.focus();
        return;
      }
      open(dialog, (value) => {
        if (value !== "accept") return;
        accept();
        go();
      });
    });
  }

  // When the terms were accepted, in the farm's time zone, e.g. "October 6, 2026 at 3:42 PM PDT"
  termsTimestamp() {
    try {
      return new Date().toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      });
    } catch (error) {
      return new Date().toISOString();
    }
  }

  // On phones each cart row's photo is a square as tall as the row (templates/cart.liquid). Its width
  // sets how wide the text beside it is, which sets the row's height, so set the width to the text's
  // height and repeat until it settles; a wider photo only ever makes the text taller, so it does,
  // in a pass or two. The photo never grows so wide that the price, stepper and trash line would have
  // to wrap (measured from that line's own width), nor past 45% of the row. Wider screens use the CSS size.
  sizeCartPhotos() {
    const phone = window.matchMedia("(max-width: 639px)").matches;
    document.querySelectorAll("[data-cart-item]").forEach((row) => {
      const photo = row.querySelector("[data-cart-photo]");
      const details = row.querySelector("[data-cart-details]");
      if (!photo || !details) return;
      if (!phone) {
        photo.style.width = "";
        return;
      }
      // The width the price-and-controls line needs: each item's own width, the gap between them, and
      // the column's side padding
      const line = details.lastElementChild;
      const lineStyle = getComputedStyle(line);
      const columnStyle = getComputedStyle(details);
      const needed =
        Array.from(line.children).reduce((sum, child) => sum + child.offsetWidth, 0) +
        (parseFloat(lineStyle.columnGap) || 0) * Math.max(0, line.children.length - 1) +
        parseFloat(columnStyle.paddingLeft) +
        parseFloat(columnStyle.paddingRight) +
        2;
      const widest = Math.min(row.clientWidth * 0.45, row.clientWidth - needed);
      for (let pass = 0; pass < 4; pass++) {
        const width = Math.max(0, Math.min(details.offsetHeight, widest));
        if (Math.abs(photo.offsetWidth - width) < 1) break;
        photo.style.width = `${width}px`;
      }
    });
  }

  watchCartPhotos() {
    this.sizeCartPhotos();
    let queued = false;
    const resize = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.sizeCartPhotos();
      });
    };
    window.addEventListener("resize", resize);
    // Fonts arriving change how the names wrap
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(resize);
  }

  async updateItemQuantity(itemKey, change) {
    const quantityInput = document.querySelector(
      `input[data-item-key="${itemKey}"]`
    );
    if (!quantityInput) return;

    const currentQuantity = parseInt(quantityInput.value) || 1;
    const newQuantity = Math.max(0, currentQuantity + change);

    // Update the input field immediately for instant visual feedback
    quantityInput.value = newQuantity;

    // Add visual feedback with a subtle animation
    this.addQuantityChangeAnimation(quantityInput);

    if (newQuantity === 0) {
      const removed = await this.removeItem(itemKey);
      if (!removed) quantityInput.value = currentQuantity;
    } else {
      await this.updateItemQuantityDirect(itemKey, newQuantity);
    }
  }

  async updateItemQuantityDirect(itemKey, newQuantity) {
    try {
      this.setLoadingState(itemKey, true);

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: {
            [itemKey]: newQuantity,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();
      this.updateCartUI(cart);
      this.updateCartBadge(cart.item_count);
    } catch (error) {
      console.error("Error updating cart:", error);
      this.showError("Failed to update cart. Please try again.");
    } finally {
      this.setLoadingState(itemKey, false);
    }
  }

  // Resolves true if the item was removed, false if the shopper kept it or the update failed
  async removeItem(itemKey) {
    const row = document.querySelector(`[data-cart-item="${itemKey}"]`);
    if (!(await this.confirmRemove(row ? row.dataset.itemTitle : ""))) {
      return false;
    }

    try {
      this.setLoadingState(itemKey, true);

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: {
            [itemKey]: 0,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      this.updateCartUI(cart);
      this.updateCartBadge(cart.item_count);
      return true;
    } catch (error) {
      console.error("Error removing item:", error);
      this.showError("Failed to remove item. Please try again.");
      return false;
    } finally {
      this.setLoadingState(itemKey, false);
    }
  }

  // The remove confirmation (snippets/cart-remove-modal.liquid). Resolves true for Remove; Keep it,
  // Escape or a click outside the dialog resolve false.
  confirmRemove(title) {
    const dialog = document.getElementById("cart-remove-dialog");
    if (!dialog || typeof dialog.showModal !== "function") {
      return Promise.resolve(window.confirm("Remove this item from your cart?"));
    }
    const name = dialog.querySelector("[data-cart-remove-name]");
    if (name) name.textContent = title || "This item";
    dialog.returnValue = "";
    return new Promise((resolve) => {
      const outside = (event) => {
        if (event.target === dialog) dialog.close("keep");
      };
      dialog.addEventListener("click", outside);
      dialog.addEventListener(
        "close",
        () => {
          dialog.removeEventListener("click", outside);
          resolve(dialog.returnValue === "remove");
        },
        { once: true }
      );
      dialog.showModal();
    });
  }

  setLoadingState(itemKey, isLoading) {
    const itemContainer = document.querySelector(
      `[data-cart-item="${itemKey}"]`
    );
    const quantityContainer = document
      .querySelector(`input[data-item-key="${itemKey}"]`)
      ?.closest(".flex.items-center.border-2");

    if (itemContainer) {
      itemContainer.style.opacity = isLoading ? "0.6" : "1";
      itemContainer.style.pointerEvents = isLoading ? "none" : "auto";
    }

    if (quantityContainer) {
      quantityContainer.style.opacity = isLoading ? "0.6" : "1";
      quantityContainer.style.pointerEvents = isLoading ? "none" : "auto";
    }
  }

  updateCartUI(cart) {
    // Update cart totals
    const subtotalElement = document.querySelector("[data-cart-subtotal]");
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.total_price);
    }

    const totalElement = document.querySelector("[data-cart-total]");
    if (totalElement) {
      totalElement.textContent = this.formatMoney(cart.total_price);
    }

    // Update item counts
    const itemCountElement = document.querySelector("[data-cart-item-count]");
    if (itemCountElement) {
      itemCountElement.textContent = cart.item_count;
    }

    // Get all current cart item elements
    const currentItemElements = document.querySelectorAll("[data-cart-item]");
    const currentItemKeys = Array.from(currentItemElements).map(
      (el) => el.dataset.cartItem
    );

    // Get the item keys from the updated cart
    const updatedItemKeys = cart.items.map((item) => item.key);

    // Remove items that are no longer in the cart (when the cart is now empty, the whole cart fades
    // out instead; see showEmptyCart)
    currentItemKeys.forEach((itemKey) => {
      if (cart.item_count > 0 && !updatedItemKeys.includes(itemKey)) {
        const itemElement = document.querySelector(
          `[data-cart-item="${itemKey}"]`
        );
        if (itemElement) this.collapseCartRow(itemElement);
      }
    });

    // Update individual item quantities to ensure they match the server state
    cart.items.forEach((item) => {
      const quantityInput = document.querySelector(
        `input[data-item-key="${item.key}"]`
      );
      if (quantityInput) {
        quantityInput.value = item.quantity;
      }
    });

    if (cart.item_count === 0) {
      this.showEmptyCart();
    }
  }

  // Slide a removed row out to the side as it fades, then close up the space it left so the rows below
  // glide up. The rows are spaced with margins on every row but the first, so when the first row goes,
  // its spacing is taken back through a negative bottom margin, which the next row's lost top margin
  // replaces at the same moment.
  collapseCartRow(row) {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof row.animate !== "function") {
      row.remove();
      return;
    }
    row.style.pointerEvents = "none";
    const cs = getComputedStyle(row);
    const next = row.nextElementSibling;
    const first = !row.previousElementSibling;
    const gap = first && next ? parseFloat(getComputedStyle(next).marginTop) || 0 : 0;
    const height = row.offsetHeight;

    row
      .animate(
        [
          { opacity: cs.opacity, transform: "none" },
          { opacity: 0, transform: "translateX(-28px)" },
        ],
        { duration: 240, easing: "ease-in", fill: "forwards" }
      )
      .finished.then(() => {
        row.style.overflow = "hidden";
        return row.animate(
          [
            {
              height: `${height}px`,
              marginTop: cs.marginTop,
              marginBottom: "0px",
              paddingTop: cs.paddingTop,
              paddingBottom: cs.paddingBottom,
              borderTopWidth: cs.borderTopWidth,
              borderBottomWidth: cs.borderBottomWidth,
            },
            {
              height: "0px",
              marginTop: "0px",
              marginBottom: `${-gap}px`,
              paddingTop: "0px",
              paddingBottom: "0px",
              borderTopWidth: "0px",
              borderBottomWidth: "0px",
            },
          ],
          { duration: 320, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "forwards" }
        ).finished;
      })
      .catch(() => {})
      .then(() => row.remove());
  }

  // Fade the cart out and the "Your cart is empty" message in, easing the page's height between them
  showEmptyCart() {
    const filled = document.querySelector("[data-cart-filled]");
    const empty = document.querySelector("[data-cart-empty]");
    if (!filled || !empty) {
      window.location.reload();
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof filled.animate !== "function") {
      filled.remove();
      empty.hidden = false;
      return;
    }

    const wrap = filled.parentElement;
    const before = wrap.offsetHeight;
    filled
      .animate(
        [
          { opacity: 1, transform: "none" },
          { opacity: 0, transform: "translateY(-8px)" },
        ],
        { duration: 280, easing: "ease-in", fill: "forwards" }
      )
      .finished.then(() => {
        filled.remove();
        empty.hidden = false;
        const after = wrap.offsetHeight;
        wrap.style.overflow = "hidden";
        wrap
          .animate([{ height: `${before}px` }, { height: `${after}px` }], {
            duration: 380,
            easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
          })
          .finished.then(() => {
            wrap.style.overflow = "";
          });
        empty.animate(
          [
            { opacity: 0, transform: "translateY(12px)" },
            { opacity: 1, transform: "none" },
          ],
          { duration: 450, delay: 120, easing: "ease-out", fill: "backwards" }
        );
      });
  }

  // options.deferBadge: leave the header count alone, for a caller that updates it later (see flyToCart)
  async add(variantId, quantity, options = {}) {
    const fallback = "Unable to add item to cart. Please try again.";
    let response;
    try {
      response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: Number(quantity) }],
        }),
      });
    } catch (error) {
      return { ok: false, status: 0, message: fallback };
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        ok: false,
        status: response.status,
        message: data.description || data.message || fallback,
      };
    }

    let itemCount = (Number(window.cartItemCount) || 0) + Number(quantity);
    try {
      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then(
        (r) => r.json()
      );
      itemCount = cart.item_count;
    } catch (error) {
      // The add succeeded; an estimated badge beats reporting a failure.
    }
    window.cartItemCount = itemCount;
    if (!options.deferBadge) this.updateCartBadge(itemCount);
    return { ok: true, itemCount };
  }

  // Flowers pop out of `from` (the add button) and float into the cart truck that is on screen: one
  // per item added, up to eight, all at once. Each flower takes a different one of four paths, in a random
  // order (see flightPath). The flowers take their colours from options.image, a product photo URL (see
  // flowerPalette), or are peach without one. onLand(landed, total) runs as each one lands, so the
  // caller can count up. Resolves when the last lands, or at once (after one onLand call) with
  // reduced motion or when no truck is visible.
  async flyToCart(from, count = 1, onLand = () => {}, options = {}) {
    const truck = Array.from(document.querySelectorAll("[data-cart-truck]")).find(
      (t) => t.getClientRects().length > 0 && t.getBoundingClientRect().width > 0
    );
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!from || !truck || reduced || typeof document.body.animate !== "function") {
      onLand(1, 1);
      return Promise.resolve();
    }

    const total = Math.max(1, Math.min(8, Math.floor(Number(count)) || 1));
    // Deal each flower its own path: the four in a shuffled order, reshuffled for each further four, so
    // up to four flowers never share a path and the fan spreads any that repeat
    const styles = [];
    while (styles.length < total) {
      const deck = Cart.FLIGHTS.slice();
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }
      styles.push(...deck);
    }
    // Usually ready from the page's preload; never hold the flowers back more than 150ms for it
    const palette = options.image
      ? await Promise.race([
          this.flowerPalette(options.image).catch(() => null),
          new Promise((resolve) => setTimeout(() => resolve(null), 150)),
        ])
      : null;
    let landed = 0;
    const flights = [];
    for (let i = 0; i < total; i++) {
      flights.push(
        this.flyOne(from, truck, i, total, styles[i], palette).then(() => {
          landed += 1;
          onLand(landed, total);
        })
      );
    }
    return Promise.all(flights);
  }

  // One flower's flight for flyToCart. `index` of `total` fans the paths out so they don't overlap.
  flyOne(from, truck, index, total, style, palette) {
    const size = 40;
    const start = from.getBoundingClientRect();
    const end = truck.getBoundingClientRect();
    const x0 = start.left + start.width / 2 - size / 2;
    const y0 = start.top + start.height / 2 - size / 2;
    // The middle of the truck bed: the left half of the icon, just above the side panel
    const dx = end.left + end.width * 0.32 - size / 2 - x0;
    const dy = end.top + end.height * 0.32 - size / 2 - y0;

    // With several flowers, spread them sideways and in height, most in mid-flight and not at all at
    // either end. The fan stays within about 300px however many flowers there are.
    const step = total > 1 ? Math.min(48, 300 / (total - 1)) : 0;
    const spread = (index - (total - 1) / 2) * step;
    const lift = (index % 2 ? 45 : -25) - 8 * Math.min(index, 3);

    // Swing toward the roomier side; if the flight would still leave the screen, try the other side
    const roomier = x0 + size / 2 > window.innerWidth / 2 ? -1 : 1;
    const build = (side) => this.flightPath(style, dx, dy, side, spread, lift, 8 - y0);
    const overflow = (flight) => {
      let worst = 0;
      for (let i = 0; i <= 60; i++) {
        const p = flight.at(i / 60);
        worst = Math.max(worst, 6 - (x0 + p.x), x0 + p.x + size + 6 - window.innerWidth, 4 - (y0 + p.y));
      }
      return worst;
    };
    let path = build(roomier);
    if (overflow(path) > 0) {
      const other = build(-roomier);
      if (overflow(other) < overflow(path)) path = other;
    }

    const flower = document.createElement("div");
    flower.className = "cart-flight";
    flower.setAttribute("aria-hidden", "true");
    flower.style.left = `${x0}px`;
    flower.style.top = `${y0}px`;
    flower.innerHTML = this.flowerSvg(size, palette || Cart.PEACH);
    document.body.appendChild(flower);

    const easeOutBack = (t) => 1 + 2.2 * Math.pow(t - 1, 3) + 1.2 * Math.pow(t - 1, 2);
    const smooth = (t) => t * t * (3 - 2 * t);
    const frames = [];
    const steps = 90;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const { x, y } = path.at(t);
      const grow = t < 0.22 ? easeOutBack(t / 0.22) : 1;
      const shrink = t > 0.7 ? 1 - 0.68 * smooth((t - 0.7) / 0.3) : 1;
      const turn = (index % 2 ? -1 : 1) * (220 - 25 * (index % 3)) * t + 8 * Math.sin(t * Math.PI * 3 + index);
      frames.push({
        transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) rotate(${turn.toFixed(1)}deg) scale(${(grow * shrink).toFixed(3)})`,
      });
    }

    const flight = flower.animate(frames, { duration: 1300, easing: "linear", fill: "forwards" });
    return flight.finished
      .catch(() => {})
      .then(() => flower.remove());
  }

  // The four flights from the button (0, 0) to the truck bed (dx, dy). Returns { at(t) }, the offset
  // at time t from 0 to 1. Each is a smooth curve travelled at an eased, even pace, plus for two of
  // them a motion layered on in time, which keeps it smooth however tight the route:
  // - arc: a high, smooth arc
  // - loop: a smaller arc with a loop-the-loop around the middle
  // - flutter: a gentle arc with a side-to-side drift, like a falling petal
  // - swoop: a wide swing out to the side, then a sweep into the bed
  // `side` (1 or -1) is the screen side to swing toward; no point rises above minY.
  flightPath(style, dx, dy, side, spread, lift, minY = -Infinity) {
    const bezier = (t, a, b, c, d) => {
      const u = 1 - t;
      return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d;
    };
    const smooth = (t) => t * t * (3 - 2 * t);
    const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const route = Math.hypot(dx, dy) || 1;
    // Unit vector across the route, pointing toward `side` on screen
    let nx = -dy / route;
    let ny = dx / route;
    if (nx * side < 0 || (nx === 0 && ny > 0)) {
      nx = -nx;
      ny = -ny;
    }
    const nearlyVertical = Math.abs(dx) < 120;

    let p1;
    let p2;
    if (style === "swoop") {
      p1 = { x: side * 170, y: -60 };
      p2 = { x: dx + side * 150, y: dy + (dy < 0 ? 40 : -40) };
    } else if (style === "loop" || style === "flutter") {
      p1 = nearlyVertical ? { x: side * 45, y: -100 } : { x: dx * 0.25, y: -100 };
      p2 = nearlyVertical ? { x: dx + side * 45, y: dy - 70 } : { x: dx * 0.8, y: dy - 80 };
    } else {
      p1 = nearlyVertical ? { x: side * 75, y: -120 } : { x: dx * 0.15, y: -130 };
      p2 = nearlyVertical ? { x: dx + side * 75, y: dy - 80 } : { x: dx + (dx >= 0 ? -1 : 1) * 30, y: dy - 110 };
    }
    // Keep the curve below the top of the window (it never rises above its control points)
    const room = minY + 34;
    p1.y = Math.max(p1.y, room);
    p2.y = Math.max(p2.y, room);

    // The curve, with the fan for several flowers, measured by distance travelled
    const points = [];
    for (let i = 0; i <= 240; i++) {
      const s = i / 240;
      const bulge = Math.sin(Math.PI * s);
      const x = bezier(s, 0, p1.x, p2.x, dx) + nx * spread * bulge;
      const y = bezier(s, 0, p1.y, p2.y, dy) + ny * spread * bulge + lift * bulge * 0.5;
      const prev = points[i - 1];
      points.push({ x, y, d: prev ? prev.d + Math.hypot(x - prev.x, y - prev.y) : 0 });
    }
    const length = points[points.length - 1].d;
    const along = (distance) => {
      let i = 1;
      while (i < points.length - 1 && points[i].d < distance) i++;
      const a = points[i - 1];
      const b = points[i];
      const f = b.d === a.d ? 0 : (distance - a.d) / (b.d - a.d);
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
    };

    // Motions layered on in time. Each starts and ends at rest, so the flight stays smooth.
    let extra = () => ({ x: 0, y: 0 });
    if (style === "loop") {
      // One full circle between 18% and 62% of the time, rising over the top first
      const r = Math.min(46, Math.max(28, route * 0.09));
      const fx = dx / route;
      const fy = dy / route;
      let ux = -fy;
      let uy = fx;
      if (uy > 0) {
        ux = -ux;
        uy = -uy;
      }
      extra = (t) => {
        const w = t < 0.18 ? 0 : t > 0.62 ? 1 : smooth((t - 0.18) / 0.44);
        const a = 2 * Math.PI * w;
        return {
          x: r * (Math.sin(a) * fx + (1 - Math.cos(a)) * ux),
          y: r * (Math.sin(a) * fy + (1 - Math.cos(a)) * uy),
        };
      };
    } else if (style === "flutter") {
      // Three gentle swings side to side, strongest mid-flight
      extra = (t) => {
        const sway = 24 * Math.sin(3 * Math.PI * t) * Math.sin(Math.PI * t);
        return { x: nx * sway, y: ny * sway * 0.4 };
      };
    }

    return {
      at: (t) => {
        const p = along(easeInOut(t) * length);
        const e = extra(t);
        return { x: p.x + e.x, y: p.y + e.y };
      },
    };
  }

  // The main flower colours in a product photo, as a palette for flowerSvg. The photo is drawn small
  // onto a canvas (Shopify serves it from the store's own domain, so its pixels can be read), and
  // pixels near the middle, where the blooms usually are, count most. Greens (leaves and stems), grey
  // background and near-black are skipped; what is left is grouped by hue. The biggest group colours
  // the petals and a second, different hue the inner petals. A mostly white or cream middle makes a
  // white flower. Resolves to null if the photo can't be read. Cached per URL.
  flowerPalette(url) {
    this.palettes = this.palettes || new Map();
    if (!this.palettes.has(url)) {
      this.palettes.set(
        url,
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              resolve(this.paletteFromImage(img));
            } catch (error) {
              resolve(null);
            }
          };
          img.onerror = reject;
          img.src = url;
        })
      );
    }
    return this.palettes.get(url);
  }

  paletteFromImage(img) {
    const n = 48;
    const canvas = document.createElement("canvas");
    canvas.width = n;
    canvas.height = n;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, n, n);
    const data = ctx.getImageData(0, 0, n, n).data;

    const bins = Array.from({ length: 24 }, () => ({ w: 0, r: 0, g: 0, b: 0 }));
    let colourful = 0;
    let white = 0;
    const sigma = n * 0.32;
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const k = (y * n + x) * 4;
        const r = data[k] / 255;
        const g = data[k + 1] / 255;
        const b = data[k + 2] / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const l = (max + min) / 2;
        const chroma = max - min; // how much colour, whatever the lightness
        const dist2 = (x - n / 2) ** 2 + (y - n / 2) ** 2;
        const weight = Math.exp(-dist2 / (2 * sigma * sigma));
        let hue = 0;
        if (chroma > 0) {
          if (max === r) hue = ((g - b) / chroma) % 6;
          else if (max === g) hue = (b - r) / chroma + 2;
          else hue = (r - g) / chroma + 4;
          hue = (hue * 60 + 360) % 360;
        }
        // White and cream petals: light, and either nearly colourless or a pale yellowish cream
        if (l > 0.66 && (chroma < 0.1 || (hue >= 30 && hue <= 75 && chroma < 0.24))) {
          white += weight;
          continue;
        }
        if (chroma < 0.12 || l < 0.16 || l > 0.95) continue; // grey background, shadow, glare
        if (hue > 65 && hue < 170) continue; // leaves and stems
        const sat = chroma;
        const bin = bins[Math.floor(hue / 15)];
        const wv = weight * sat; // vivid pixels say more about the bloom than washed-out ones
        bin.w += wv;
        bin.r += data[k] * wv;
        bin.g += data[k + 1] * wv;
        bin.b += data[k + 2] * wv;
        colourful += wv;
      }
    }

    if (colourful < white * 0.3) return Cart.WHITE;
    // Merge each bin with its neighbours, so a hue split across two bins still wins
    const scored = bins
      .map((bin, i) => {
        const near = [bins[(i + 23) % 24], bin, bins[(i + 1) % 24]];
        const w = near.reduce((sum, b) => sum + b.w, 0);
        return { i, w, bin };
      })
      .filter((x) => x.bin.w > 0)
      .sort((a, b) => b.w - a.w);
    if (!scored.length) return null;

    const rgbOf = (bin) => [bin.r / bin.w, bin.g / bin.w, bin.b / bin.w];
    const main = rgbOf(scored[0].bin);
    const second = scored.find((x) => {
      const apart = Math.min(Math.abs(x.i - scored[0].i), 24 - Math.abs(x.i - scored[0].i));
      return apart >= 3 && x.w > scored[0].w * 0.3;
    });
    const accent = second ? rgbOf(second.bin) : null;
    const [mr, mg, mb] = main;
    const mmax = Math.max(mr, mg, mb);
    const mmin = Math.min(mr, mg, mb);
    let mainHue = 0;
    if (mmax > mmin) {
      if (mmax === mr) mainHue = ((mg - mb) / (mmax - mmin)) % 6;
      else if (mmax === mg) mainHue = (mb - mr) / (mmax - mmin) + 2;
      else mainHue = (mr - mg) / (mmax - mmin) + 4;
      mainHue = (mainHue * 60 + 360) % 360;
    }
    // Yellow and cream petals get a brown centre to stand out, and shade toward amber, not black,
    // which would turn them olive
    const yellowish = mainHue >= 30 && mainHue <= 75;

    const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);
    const hex = (c) =>
      "#" + c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");
    const black = yellowish ? [150, 85, 25] : [40, 28, 30];
    const light = [255, 248, 240];
    const innerBase = accent || mix(main, light, 0.35);
    return {
      outerIn: hex(mix(main, black, 0.25)),
      outerOut: hex(mix(main, light, 0.2)),
      innerIn: hex(mix(innerBase, black, 0.1)),
      innerOut: hex(mix(innerBase, light, 0.55)),
      edge: hex(mix(main, black, 0.55)),
      centreIn: yellowish ? "#C2702A" : "#F7D774",
      centreOut: yellowish ? "#7A3E18" : "#D49A2E",
    };
  }

  // A ranunculus-style bloom in `palette`'s colours: eight shaded outer petals, eight lighter inner
  // ones set between them, and a centre with seed dots. Ids are unique per flower.
  flowerSvg(size, palette) {
    this.flowerId = (this.flowerId || 0) + 1;
    const id = `cart-flower-${this.flowerId}`;
    const c = palette;
    const petal = "M12 12C9.7 9.4 9.6 4.2 12 2.4C14.4 4.2 14.3 9.4 12 12Z";
    const ring = (angles, scale, fill) =>
      angles
        .map(
          (a) =>
            `<path d="${petal}" fill="url(#${fill})" stroke="${c.edge}" stroke-opacity="0.22" stroke-width="0.25" transform="rotate(${a} 12 12) translate(12 12) scale(${scale}) translate(-12 -12)"/>`
        )
        .join("");
    const outer = [0, 45, 90, 135, 180, 225, 270, 315];
    const inner = outer.map((a) => a + 22.5);
    const seeds = [0, 72, 144, 216, 288]
      .map((a) => {
        const r = (a * Math.PI) / 180;
        return `<circle cx="${(12 + 1.1 * Math.cos(r)).toFixed(2)}" cy="${(12 + 1.1 * Math.sin(r)).toFixed(2)}" r="0.32" fill="#5a3a12" fill-opacity="0.7"/>`;
      })
      .join("");
    return (
      `<svg viewBox="0 0 24 24" width="${size}" height="${size}">` +
      `<defs>` +
      `<radialGradient id="${id}-o" cx="12" cy="12" r="10" gradientUnits="userSpaceOnUse"><stop offset="0.25" stop-color="${c.outerIn}"/><stop offset="1" stop-color="${c.outerOut}"/></radialGradient>` +
      `<radialGradient id="${id}-i" cx="12" cy="12" r="7" gradientUnits="userSpaceOnUse"><stop offset="0.2" stop-color="${c.innerIn}"/><stop offset="1" stop-color="${c.innerOut}"/></radialGradient>` +
      `<radialGradient id="${id}-c" cx="11.4" cy="11.4" r="2.6" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="${c.centreIn}"/><stop offset="1" stop-color="${c.centreOut}"/></radialGradient>` +
      `</defs>` +
      ring(outer, 1, `${id}-o`) +
      ring(inner, 0.66, `${id}-i`) +
      `<circle cx="12" cy="12" r="2.3" fill="url(#${id}-c)"/>` +
      seeds +
      `</svg>`
    );
  }

  bindAddedModal() {
    const modal = document.getElementById("add-to-cart-alert");
    if (!modal) return;

    const close = () => this.hideAdded();
    document
      .getElementById("add-to-cart-alert-backdrop")
      ?.addEventListener("click", close);
    document
      .getElementById("add-to-cart-alert-close")
      ?.addEventListener("click", close);

    document.addEventListener("keydown", (event) => {
      if (modal.getAttribute("aria-hidden") !== "false") return;
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      } else if (event.key === "Tab") {
        const focusable = modal.querySelectorAll("a[href], button:not([disabled])");
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  }

  showAdded(opener = document.activeElement) {
    const modal = document.getElementById("add-to-cart-alert");
    if (!modal) return;
    this.addedOpener = opener;
    modal.removeAttribute("inert");
    modal.classList.remove("opacity-0", "pointer-events-none");
    modal.classList.add("opacity-100");
    modal.setAttribute("aria-hidden", "false");
    const content = document.getElementById("add-to-cart-alert-content");
    if (content) {
      content.classList.remove("scale-95");
      content.classList.add("scale-100");
    }
    modal.querySelector("a[href], button")?.focus();
  }

  hideAdded() {
    const modal = document.getElementById("add-to-cart-alert");
    if (!modal || modal.getAttribute("aria-hidden") === "true") return;
    modal.classList.add("opacity-0", "pointer-events-none");
    modal.classList.remove("opacity-100");
    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("inert", "");
    const content = document.getElementById("add-to-cart-alert-content");
    if (content) {
      content.classList.add("scale-95");
      content.classList.remove("scale-100");
    }
    const opener = this.addedOpener;
    this.addedOpener = null;
    if (opener && typeof opener.focus === "function" && opener.isConnected) {
      opener.focus();
    }
  }

  updateCartBadge(itemCount) {
    const badges = ["cartBadge", "topCartBadge"];
    console.log("Updating cart badge", itemCount);
    badges.forEach((id) => {
      const badge = document.getElementById(id);
      if (badge) {
        if (itemCount > 0) {
          badge.textContent = itemCount;
          badge.classList.remove("hidden");
        } else {
          badge.classList.add("hidden");
        }
      }
    });
    this.updateCartTrucks(itemCount);
  }

  // Each truck bed (snippets/cart-truck.liquid) is full of flowers whenever the cart has anything in it.
  // On the first add the flowers pop in one after another; every add makes the truck hop.
  updateCartTrucks(itemCount) {
    const count = Number(itemCount) || 0;
    document.querySelectorAll("[data-cart-truck]").forEach((truck) => {
      const before = Number(truck.dataset.count) || 0;
      truck.dataset.count = String(count);
      const blooms = truck.querySelectorAll(".cart-truck__bloom");
      blooms.forEach((bloom, i) => {
        bloom.toggleAttribute("data-off", count === 0);
        bloom.classList.remove("is-new");
        if (before === 0 && count > 0) {
          bloom.getBoundingClientRect(); // restart the animation
          bloom.style.animationDelay = `${i * 70}ms`;
          bloom.classList.add("is-new");
        }
      });
      if (count > before) {
        truck.classList.remove("cart-truck--hop");
        truck.getBoundingClientRect();
        truck.classList.add("cart-truck--hop");
      }
    });
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  }

  addQuantityChangeAnimation(quantityInput) {
    // Add a subtle highlight animation to show the quantity changed
    quantityInput.style.transition = "background-color 0.3s ease";
    quantityInput.style.backgroundColor = "#fef3c7"; // Light yellow highlight

    setTimeout(() => {
      quantityInput.style.backgroundColor = "transparent";
    }, 300);
  }

  showError(message) {
    // Simple error display - you can enhance this with a toast notification
    alert(message);
  }
}

// The flight paths flyToCart picks from at random, drawn in flightPath()
Cart.FLIGHTS = ["arc", "loop", "flutter", "swoop"];

// Flower colours when a product has no photo, or its photo can't be read
Cart.PEACH = {
  outerIn: "#C95A4C", outerOut: "#F2A08F", innerIn: "#E8867B", innerOut: "#FFE0D2",
  edge: "#B84A3E", centreIn: "#F7D774", centreOut: "#D49A2E",
};

// For photos whose middle is mostly white: white petals shaded with cream
Cart.WHITE = {
  outerIn: "#E9DFCF", outerOut: "#FFFFFF", innerIn: "#F4ECDD", innerOut: "#FFFFFF",
  edge: "#9C8B70", centreIn: "#F7D774", centreOut: "#D49A2E",
};

// Initialize cart when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  window.themeCart = new Cart();
});

// Export for use in other scripts
window.Cart = Cart;
